import {} from "react";

// ============================================================
// SHARED DESIGN TOKENS
// One source of truth for card shadow, radius, title color,
// and bold/strong text color across Payables, Receivables,
// and the CFO Dashboard. Change a value here and it updates
// everywhere these primitives are used.
// ============================================================

// ============================================================
// SHARED DESIGN TOKENS (GX TYPOGRAPHY SYSTEM)
// ============================================================

export const tokens = {
  shadow: "0 1px 2px rgba(15,23,42,0.04), 0 4px 16px rgba(15,23,42,0.04)",
  cardBorder: "1px solid rgba(15,23,42,0.08)",
  radius: 12,
  cardPadding: "18px 20px",

  // GX Fonts
  fontRegular: "var(--font-gx-regular)",
  fontMedium: "var(--font-gx-medium)",
  fontSemibold: "var(--font-gx-semibold)",
  fontBold: "var(--font-gx-bold)",
  fontExtraBold: "var(--font-gx-extrabold)",

  // Colors
  titleColor: "#111827",
  subColor: "#6B7280",
  boldColor: "#111827",
  boldWeight: 600,
  dangerColor: "#DC2626",
  mutedColor: "#6B7280",
  border: "#F3F4F6",

  // GX Typography Scale
  titleSize: "0.938rem", // gx-p15-sb
  titleWeight: 400,
  titleLineHeight: "22px",

  subSize: "0.75rem", // gx-p12-r
  subLineHeight: "18px",

  bodySize: "0.813rem", // gx-p13-r
  bodyLineHeight: "20px",

  metricSize: "1.25rem", // gx-p20-b
  metricWeight: 600,

  badgeSize: "0.688rem", // gx-p11-sb
  sectionLabelSize: "0.625rem", // gx-p10-sb
};

// ============================================================
// BASE CARD
// ============================================================

export const cardBase = {
  background: "#fff",
  borderRadius: tokens.radius,
  padding: tokens.cardPadding,
  boxShadow: tokens.shadow,
  border: tokens.cardBorder,
};

// ============================================================
// TYPOGRAPHY STYLES
// ============================================================

export const cardTitleStyle = {
  fontFamily: tokens.fontSemibold,
  fontWeight: tokens.titleWeight,
  fontSize: tokens.titleSize,
  color: tokens.titleColor,
  lineHeight: tokens.titleLineHeight,
};

export const cardSubStyle = {
  fontFamily: tokens.fontRegular,
  fontWeight: 400,
  fontSize: tokens.subSize,
  color: tokens.subColor,
  lineHeight: tokens.subLineHeight,
};

export const sectionLabelStyle = {
  fontFamily: tokens.fontBold,
  fontSize: tokens.sectionLabelSize,
  fontWeight: 400,
  color: tokens.subColor,
  letterSpacing: 1,
  marginBottom: 12,
  textTransform: "uppercase",
  lineHeight: "16px",
};

export const thStyle = {
  fontFamily: tokens.fontSemibold,
  textAlign: "left",
  fontSize: tokens.subSize,
  fontWeight: 400,
  color: tokens.subColor,
  padding: "4px 8px 4px 0",
  borderBottom: `1px solid ${tokens.border}`,
  lineHeight: "18px",
};

export const tdStyle = {
  fontFamily: tokens.fontRegular,
  fontSize: tokens.bodySize,
  fontWeight: 400,
  padding: "7px 8px 7px 0",
  color: "#374151",
  borderBottom: `0.5px solid ${tokens.border}`,
  lineHeight: tokens.bodyLineHeight,
};

export const boldCellStyle = (danger = false) => ({
  fontFamily: tokens.fontBold,
  fontWeight: tokens.boldWeight,
  color: danger ? tokens.dangerColor : tokens.boldColor,
});

export const metricLabelStyle = {
  fontFamily: tokens.fontMedium,
  fontSize: tokens.badgeSize,
  fontWeight: 400,
  color: tokens.subColor,
  lineHeight: "16px",
};

export const metricValueStyle = {
  fontFamily: tokens.fontBold,
  fontSize: tokens.metricSize,
  fontWeight: tokens.metricWeight,
  color: tokens.titleColor,
  lineHeight: 1.2,
};

export const metricAlertStyle = {
  fontFamily: tokens.fontMedium,
  fontSize: tokens.subSize,
  fontWeight: 400,
  color: tokens.dangerColor,
  lineHeight: "18px",
};

// ============================================================
// SHARED PRIMITIVES
// ============================================================

export const Badge = ({ type }) => {
  const styles = {
    QUERY: { background: "#E8F0FE", color: "#1967D2" },
    AI: { background: "#F3E8FF", color: "#7C3AED" },
  };
  return (
    <span
      style={{
        ...styles[type],
        fontFamily: tokens.fontSemibold,
        fontSize: 10,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 8,
        letterSpacing: 0.5,
        lineHeight: "16px",
        whiteSpace: "nowrap",
        display: "inline-block",
      }}
    >
      {type}
    </span>
  );
};

export const Card = ({ children, style }) => (
  <div style={{ ...cardBase, ...style }}>{children}</div>
);

export const CardHeader = ({ title, sub, badge }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: 14,
      gap: 12,
    }}
  >
    <div>
      <div style={cardTitleStyle}>{title}</div>
      {sub && <div style={{ ...cardSubStyle, marginTop: 2 }}>{sub}</div>}
    </div>
    {badge}
  </div>
);

export const Th = ({ children }) => <th style={thStyle}>{children}</th>;

export const Td = ({ children, style }) => (
  <td style={{ ...tdStyle, ...style }}>{children}</td>
);

// Status / risk pill. One shared color map so "High risk" and
// "Overdue" and "Yes (bottleneck)" all read the same everywhere.
const STATUS_MAP = {
  High: { bg: "#FEE2E2", color: "#DC2626" },
  Medium: { bg: "#FEF9C3", color: "#B45309" },
  Low: { bg: "#D1FAE5", color: "#059669" },
  "On track": { bg: "#D1FAE5", color: "#059669" },
  Watch: { bg: "#FEF9C3", color: "#B45309" },
  Overdue: { bg: "#FEE2E2", color: "#DC2626" },
  Close: { bg: "#FEF9C3", color: "#B45309" },
  Yes: { bg: "#FEE2E2", color: "#DC2626" },
  Maybe: { bg: "#FEF9C3", color: "#B45309" },
  No: { bg: "#D1FAE5", color: "#059669" },
  Accept: { bg: "#D1FAE5", color: "#059669" },
  Review: { bg: "#FEF9C3", color: "#B45309" },
  Declined: { bg: "#F3F4F6", color: "#6B7280" },
};

export const StatusPill = ({ level, onClick }) => {
  const s = STATUS_MAP[level] || STATUS_MAP.Low;
  const Tag = onClick ? "button" : "span";
  return (
    <Tag
      onClick={onClick}
      style={{
        background: s.bg,
        color: s.color,
        border: "none",
        borderRadius: 6,
        padding: "2px 10px",
        fontFamily: tokens.fontBold,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: "18px",
        display: "inline-block",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      {level}
    </Tag>
  );
};

export const FooterNote = ({ children }) => (
  <div
    style={{
      marginTop: 12,
      ...cardSubStyle,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 8,
    }}
  >
    {children}
  </div>
);

export const PrimaryBtn = ({ children, onClick }) => (
  <button
    onClick={onClick}
    style={{
      background: "#1967D2",
      color: "#fff",
      border: "none",
      borderRadius: 6,
      padding: "6px 14px",
      fontFamily: tokens.fontSemibold,
      fontSize: 12,
      fontWeight: 600,
      lineHeight: "18px",
      cursor: "pointer",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </button>
);

export const OutlineBtn = ({ children, onClick }) => (
  <button
    onClick={onClick}
    style={{
      background: "#fff",
      color: "#374151",
      border: "1px solid #D1D5DB",
      borderRadius: 6,
      padding: "6px 14px",
      fontFamily: tokens.fontSemibold,
      fontSize: 12,
      fontWeight: 600,
      lineHeight: "18px",
      cursor: "pointer",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </button>
);

export const MetricCard = ({
  badge,
  title,
  value,
  valueColor,
  sub,
  link,
  alert,
}) => (
  <div
    style={{
      ...cardBase,
      flex: 1,
      minWidth: 180,
      display: "flex",
      flexDirection: "column",
      gap: 4,
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <span
        style={{
          fontFamily: tokens.fontMedium,
          fontSize: 11,
          color: tokens.subColor,
          fontWeight: 500,
          lineHeight: "16px",
        }}
      >
        {title}
      </span>
      <Badge type={badge} />
    </div>
    <div
      style={{
        fontFamily: tokens.fontBold,
        fontSize: 20,
        fontWeight: 700,
        color: valueColor || tokens.titleColor,
        lineHeight: 1.2,
      }}
    >
      {value}
    </div>
    {alert && (
      <div
        style={{
          fontFamily: tokens.fontMedium,
          fontSize: 12,
          color: tokens.dangerColor,
          fontWeight: 500,
          lineHeight: "18px",
        }}
      >
        {alert}
      </div>
    )}
    {sub && !alert && <div style={cardSubStyle}>{sub}</div>}
    {link && (
      <a
        href="#"
        style={{
          fontFamily: tokens.fontSemibold,
          fontSize: 12,
          color: "#1967D2",
          textDecoration: "none",
          marginTop: 2,
          fontWeight: 600,
          lineHeight: "18px",
        }}
      >
        {link}
      </a>
    )}
  </div>
);
